import React, { useEffect, useMemo } from 'react'
import { FormValues, useAllTransactionContext } from '../index.context'
import { Controller, useWatch } from 'react-hook-form'
import CustomInput from 'src/app/shared/components/common/input'
import { validateOnBlur } from 'src/app/shared/utils/String.util'
import { useTranslation } from 'react-i18next'
import { Flex } from 'antd'
import CustomRangePicker from 'src/app/shared/components/common/range-picker'
import CustomSelect from 'src/app/shared/components/common/select'
import { inputDecimalNumber } from 'src/app/shared/utils'
import { useFormRules } from '../index.constant'
import { useWindowSize } from 'src/app/shared/hooks/useWindowSize'

const AllTransactionForm: React.FC = () => {
  const { form, gettingData, options, disabled } = useAllTransactionContext()
  const { clientOptions, accountOptions, dateTypeOptions, decisionOptions, reasonOptions } = useMemo(
    () => options,
    [options],
  )
  const { t } = useTranslation()
  const { amountTo } = useFormRules(form)
  const { mode } = useWindowSize()
  const dateType = useWatch({
    control: form.control,
    name: 'dateType',
  })

  useEffect(() => {
    if (!dateType) {
      form.resetField('date')
    }
  }, [dateType, form])

  return (
    <Flex
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
      }}
    >
      <Controller
        control={form.control}
        name='clientIds'
        render={({ field, fieldState }) => (
          <CustomSelect
            label={t('AllTransactions:title.client')}
            placeholder={t('AllTransactions:placeholder.client')}
            value={field.value}
            onChange={field.onChange}
            onBlur={() => validateOnBlur<FormValues>('clientIds', field, form)}
            error={fieldState.error}
            loading={gettingData}
            options={clientOptions}
            disabled={disabled}
            haveAllOption
            showSearch
            allowClear={!!field.value?.length}
          />
        )}
      />
      <Controller
        control={form.control}
        name='accountIds'
        render={({ field, fieldState }) => (
          <CustomSelect
            label={t('AllTransactions:title.account_name')}
            placeholder={t('AllTransactions:placeholder.account_name')}
            value={field.value}
            onChange={field.onChange}
            onBlur={() => validateOnBlur<FormValues>('accountIds', field, form)}
            error={fieldState.error}
            loading={gettingData}
            options={accountOptions}
            haveAllOption
            showSearch
            allowClear={!!field.value?.length}
          />
        )}
      />
      <Flex style={{ flexDirection: mode === 'mobile' ? 'column' : undefined, gap: 24 }}>
        <Controller
          control={form.control}
          name='dateType'
          render={({ field, fieldState }) => (
            <CustomSelect
              label={t('AllTransactions:title.date_type')}
              placeholder={t('AllTransactions:placeholder.date_type')}
              value={field.value}
              onChange={field.onChange}
              onBlur={() => validateOnBlur<FormValues>('dateType', field, form)}
              error={fieldState.error}
              options={dateTypeOptions}
              loading={gettingData}
              allowClear
            />
          )}
        />
        <Controller
          control={form.control}
          name='date'
          render={({ field, fieldState }) => (
            <CustomRangePicker
              label={t('AllTransactions:title.date')}
              allowEmpty={[true, true]}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={fieldState.error}
              allowClear
            />
          )}
        />
      </Flex>
      <Flex style={{ flexDirection: mode === 'mobile' ? 'column' : undefined, gap: 24 }}>
        <Controller
          control={form.control}
          name='amountFrom'
          render={({ field, fieldState }) => (
            <CustomInput
              label={t('AllTransactions:title.amount')}
              placeholder={t('AllTransactions:placeholder.from')}
              value={field.value}
              onChange={(e) => field.onChange(inputDecimalNumber(e), { shouldValidate: true })}
              onBlur={() => validateOnBlur<FormValues>(['amountFrom', 'amountTo'], field, form)}
              error={fieldState.error}
              suffix={'$'}
            />
          )}
        />
        <Controller
          control={form.control}
          name='amountTo'
          render={({ field, fieldState }) => (
            <div style={{ flex: 1 }}>
              <div
                style={{
                  height: 22,
                }}
              />
              <CustomInput
                placeholder={t('AllTransactions:placeholder.to')}
                value={field.value}
                onChange={(e) => field.onChange(inputDecimalNumber(e), { shouldValidate: true })}
                onBlur={() => validateOnBlur<FormValues>('amountTo', field, form)}
                error={fieldState.error}
                suffix={'$'}
              />
            </div>
          )}
          rules={amountTo}
        />
      </Flex>
      <Flex style={{ gap: 24 }}>
        <Controller
          control={form.control}
          name='decisionId'
          render={({ field, fieldState }) => (
            <CustomSelect
              label={t('AllTransactions:title.decisions')}
              placeholder={t('AllTransactions:placeholder.decisions')}
              value={field.value}
              onChange={field.onChange}
              onBlur={() => validateOnBlur<FormValues>('decisionId', field, form)}
              error={fieldState.error}
              options={decisionOptions}
              loading={gettingData}
              allowClear
            />
          )}
        />
        <Controller
          control={form.control}
          name='reasonId'
          render={({ field, fieldState }) => (
            <CustomSelect
              label={t('AllTransactions:title.reasons')}
              placeholder={t('AllTransactions:placeholder.reasons')}
              value={field.value}
              onChange={field.onChange}
              onBlur={() => validateOnBlur<FormValues>('reasonId', field, form)}
              error={fieldState.error}
              options={reasonOptions}
              loading={gettingData}
              allowClear
              showSearch
              mode='multiple'
            />
          )}
        />
      </Flex>
    </Flex>
  )
}

export default AllTransactionForm
